import { useId, useRef, useState, type ReactNode } from 'react';
import { commandForKey, type InputCommand } from './input-commands';

function targetIndex(command: InputCommand | null, index: number, count: number): number | null {
  switch (command) {
    case 'next':
      return index + 1;
    case 'previous':
      return index - 1;
    case 'first':
      return 0;
    case 'last':
      return count - 1;
    default:
      return null;
  }
}

/**
 * Accessible tabs (WAI-ARIA tabs pattern, automatic activation): one Tab stop for the tab list,
 * ←/→ (and Home/End) move between tabs through the central input commands, and the panel is
 * labelled by its tab. Panels that are not selected are not rendered.
 */
export function Tabs({
  label,
  tabs,
}: {
  label: string;
  tabs: readonly { id: string; title: string; content: ReactNode }[];
}) {
  const base = useId();
  const [selected, setSelected] = useState(tabs[0]?.id ?? '');
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const current = tabs.find((tab) => tab.id === selected) ?? tabs[0];
  if (!current) return null;

  const select = (index: number) => {
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    setSelected(tab.id);
    buttons.current[(index + tabs.length) % tabs.length]?.focus();
  };

  return (
    <div className="ui-tabs">
      <div className="ui-tabs__list" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const active = tab.id === current.id;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                buttons.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${tab.id}`}
              aria-selected={active}
              aria-controls={`${base}-panel-${tab.id}`}
              tabIndex={active ? 0 : -1}
              className="ui-tabs__tab"
              onClick={() => setSelected(tab.id)}
              onKeyDown={(event) => {
                // Only the horizontal keys move between tabs (↑/↓ keep scrolling the page).
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') return;
                const target = targetIndex(commandForKey(event), index, tabs.length);
                if (target === null) return;
                event.preventDefault();
                select(target);
              }}
            >
              {tab.title}
            </button>
          );
        })}
      </div>
      <div
        className="ui-tabs__panel"
        role="tabpanel"
        id={`${base}-panel-${current.id}`}
        aria-labelledby={`${base}-tab-${current.id}`}
        tabIndex={0}
      >
        {current.content}
      </div>
    </div>
  );
}
